import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Ship, Plus, Edit2, Trash2, Globe, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../../config';

const ShippingRules = ({ user }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newPort, setNewPort] = useState('');

  const [formData, setFormData] = useState({
    country: '',
    freight_per_m3: '',
    inspection_charges: '',
    shipping_methods: ['RoRo'],
    ports: [],
    is_active: true
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/shipping-rules`);
      setRules(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching shipping rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingRule(null);
    setFormData({
      country: '',
      freight_per_m3: '',
      inspection_charges: '',
      shipping_methods: ['RoRo'],
      ports: [],
      is_active: true
    });
    setNewPort('');
    setShowDialog(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      country: rule.country || '',
      freight_per_m3: rule.freight_per_m3?.toString() || '',
      inspection_charges: rule.inspection_charges?.toString() || '',
      shipping_methods: rule.shipping_methods || ['RoRo'],
      ports: rule.ports || [],
      is_active: rule.is_active !== false
    });
    setNewPort('');
    setShowDialog(true);
  };

  const handleAddPort = () => {
    if (newPort.trim() && !formData.ports.includes(newPort.trim())) {
      setFormData({ ...formData, ports: [...formData.ports, newPort.trim()] });
      setNewPort('');
    }
  };

  const handleRemovePort = (port) => {
    setFormData({ ...formData, ports: formData.ports.filter(p => p !== port) });
  };

  const handleToggleMethod = (method) => {
    const methods = formData.shipping_methods.includes(method)
      ? formData.shipping_methods.filter(m => m !== method)
      : [...formData.shipping_methods, method];
    if (methods.length > 0) {
      setFormData({ ...formData, shipping_methods: methods });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.country.trim()) {
      toast.error('Country name is required');
      return;
    }
    if (!formData.freight_per_m3 || parseFloat(formData.freight_per_m3) <= 0) {
      toast.error('Freight rate per M3 is required');
      return;
    }

    const data = {
      country: formData.country.trim(),
      freight_per_m3: parseFloat(formData.freight_per_m3) || 0,
      inspection_charges: parseFloat(formData.inspection_charges) || 0,
      shipping_methods: formData.shipping_methods,
      ports: formData.ports,
      is_active: formData.is_active
    };

    try {
      if (editingRule) {
        await axios.put(`${API}/shipping-rules/${editingRule.id}`, data);
        toast.success('Shipping rule updated!');
      } else {
        await axios.post(`${API}/shipping-rules`, data);
        toast.success('Shipping rule created!');
      }
      setShowDialog(false);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shipping rule');
    }
  };

  const handleDelete = async (ruleId, country) => {
    if (!window.confirm(`Delete shipping rule for ${country}?`)) return;
    try {
      await axios.delete(`${API}/shipping-rules/${ruleId}`);
      toast.success('Shipping rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete shipping rule');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse h-8 bg-slate-200 rounded w-1/4"></div>
        <div className="animate-pulse h-64 bg-slate-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Ship className="h-6 w-6 text-blue-600" />
            Shipping Rules
          </h1>
          <p className="text-slate-500 mt-1">Set freight rates per country for the public shipping calculator</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1" />
          Add Country
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rules.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Freight / M3</TableHead>
                    <TableHead>Inspection</TableHead>
                    <TableHead>Methods</TableHead>
                    <TableHead>Ports</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-slate-400" />
                          {rule.country}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-blue-600">
                        ${rule.freight_per_m3?.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {rule.inspection_charges > 0 ? (
                          <span className="text-slate-700">${rule.inspection_charges.toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(rule.shipping_methods || []).map(m => (
                            <Badge key={m} variant="secondary" className="text-xs">
                              {m}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {rule.ports && rule.ports.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {rule.ports.map(p => (
                              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={rule.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                          {rule.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(rule.id, rule.country)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Ship className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Shipping Rules</h3>
              <p className="text-slate-500 mb-4">Add countries with freight rates to enable the shipping calculator.</p>
              <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-1" />
                Add First Country
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? `Edit — ${editingRule.country}` : 'Add Shipping Rule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Country *</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="e.g., Jamaica"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Freight Rate / M3 ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.freight_per_m3}
                  onChange={(e) => setFormData({ ...formData, freight_per_m3: e.target.value })}
                  placeholder="276"
                />
              </div>
              <div>
                <Label>Inspection Charges ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.inspection_charges}
                  onChange={(e) => setFormData({ ...formData, inspection_charges: e.target.value })}
                  placeholder="280 (optional)"
                />
              </div>
            </div>

            <div>
              <Label>Shipping Methods</Label>
              <div className="flex gap-4 mt-1">
                {['RoRo', 'Container'].map(method => (
                  <div key={method} className="flex items-center space-x-2">
                    <Checkbox
                      id={`method_${method}`}
                      checked={formData.shipping_methods.includes(method)}
                      onCheckedChange={() => handleToggleMethod(method)}
                    />
                    <Label htmlFor={`method_${method}`} className="cursor-pointer">{method}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Ports (optional)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newPort}
                  onChange={(e) => setNewPort(e.target.value)}
                  placeholder="Add port name"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPort(); } }}
                />
                <Button type="button" variant="outline" onClick={handleAddPort}>Add</Button>
              </div>
              {formData.ports.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ports.map(port => (
                    <Badge key={port} variant="secondary" className="flex items-center gap-1">
                      {port}
                      <button type="button" onClick={() => handleRemovePort(port)} className="ml-1 hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active" className="cursor-pointer">Active (visible on website)</Label>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingRules;
